import { Sheet } from '../../../utils/download-utils';
import { ChannelExportModel } from './hooks/useChannelExportModels';
import { ModelType, MODEL_TYPE_ORDER, ModelTypeType } from './data-qa-constants';
import { ChannelConfig, WeekRow } from './data-qa-types';
import { filterWeeksToFencepost } from './data-qa-utils';

// Short tab-name aliases for each model type, per the requested naming convention
// (<model>-<model>_<channel>, e.g. "dtc-comm-be_affiliate" for Away, whose DTC/
// Commerce/Brand Equity models share identical Channels data).
export const MODEL_TYPE_ALIAS: Record<ModelTypeType, string> = {
  [ModelType.DTC]: 'dtc',
  [ModelType.COMMERCE]: 'comm',
  [ModelType.GEO]: 'geo',
  [ModelType.BRAND_EQUITY]: 'be'
};

export interface ChannelFingerprintGroup {
  aliasKey: string;
  channels: ChannelConfig[];
  // Already restricted to the group's fencepost window -- the same weeks the Channels
  // chart shows.
  weeks: WeekRow[];
}

// Groups models by their Channels section's content hash -- the same byte-
// identical-data signal that drives the "Same data as …" header chip
// (see useSectionDataInvariance) -- so models with identical channel data (e.g.
// Away's DTC/Commerce/Brand Equity) produce ONE combined tab set instead of
// duplicating identical rows per model. A model with no hash at all (shouldn't
// happen in practice) gets its own singleton group keyed by its config path so it
// is never silently dropped from the export. Alias order always follows
// MODEL_TYPE_ORDER regardless of the input order, so tab names are deterministic.
//
// `channels` is the UNION (by key) of every group member's own declared channel
// list, not just the first/representative model's -- a channel can be genuinely
// declared in only ONE sibling's config (e.g. a Demand Capture channel that's part
// of Commerce's taxonomy but not DTC's) even while the group's underlying week
// data is byte-identical, since the hash only reflects whichever channel keys
// carry real perChannel values, not which channels a given model's own config
// happens to declare. Using only DTC's (the group's first member in
// MODEL_TYPE_ORDER) declared channels silently dropped every such channel from
// the export (confirmed against real Away data, 2026-09-16 -- Demand Capture
// channels were missing even though the Channel Review UI, which reads each
// model's OWN config.channels rather than a shared/unioned list, showed them
// fine on Commerce's own tab).
export const groupModelsByChannelsFingerprint = (
  models: ChannelExportModel[]
): ChannelFingerprintGroup[] => {
  // The fencepost window is part of the grouping key: the backend's channels hash covers
  // every week of raw history, so two models can share it while training on different
  // windows -- and once rows are fencepost-filtered, those can no longer share a sheet.
  const byHash = new Map<string, ChannelExportModel[]>();
  for (const model of models) {
    const hash =
      model.dataset.sectionContentHash.channels ?? `__unhashed__${model.config.configPath}`;
    const { fencepostStart, fencepostEnd } = model.config;
    const key = `${hash}|${fencepostStart ?? ''}|${fencepostEnd ?? ''}|${model.dataset.reviewGranularity ?? ''}`;
    const group = byHash.get(key) ?? [];
    group.push(model);
    byHash.set(key, group);
  }

  const rawGroups = [...byHash.values()].map(group => {
    const baseAlias = MODEL_TYPE_ORDER.filter(modelType =>
      group.some(m => m.modelType === modelType)
    )
      .map(modelType => MODEL_TYPE_ALIAS[modelType])
      .join('-');
    const channelsByKey = new Map<string, ChannelConfig>();
    group.forEach(m =>
      m.config.channels.forEach(channel => {
        if (!channelsByKey.has(channel.key)) {
          channelsByKey.set(channel.key, channel);
        }
      })
    );
    const { config, dataset } = group[0];
    return {
      baseAlias,
      representativeConfig: config,
      channels: [...channelsByKey.values()],
      weeks: filterWeeksToFencepost(
        dataset.weeks,
        config.fencepostStart,
        config.fencepostEnd,
        dataset.reviewGranularity
      )
    };
  });

  // A company can have more than one DISTINCT model sharing the same model type(s) --
  // e.g. two regional GEO models, or two genuinely different clientNames appearing in
  // one export -- and baseAlias is derived purely from WHICH model types are present,
  // not which specific model. Two such groups would otherwise collide on the exact same
  // tab names (exportMultiSheetToExcel's own de-dup then papers over it with an
  // uninformative bare digit, e.g. "geo_paidSearch"/"geo_paidSearch2", with nothing
  // indicating which client/region each is). Disambiguate by regionalScope first (the
  // realistic differentiator per the reported case), falling back to clientName if THAT
  // ties too. A base alias used by only one group is left exactly as it was before.
  const countByBaseAlias = new Map<string, number>();
  rawGroups.forEach(g =>
    countByBaseAlias.set(g.baseAlias, (countByBaseAlias.get(g.baseAlias) ?? 0) + 1)
  );
  const usedAliasesByBase = new Map<string, Set<string>>();

  return rawGroups.map(({ baseAlias, representativeConfig, channels, weeks }) => {
    if ((countByBaseAlias.get(baseAlias) ?? 0) <= 1) {
      return { aliasKey: baseAlias, channels, weeks };
    }
    const usedAliases = usedAliasesByBase.get(baseAlias) ?? new Set<string>();
    const scope = representativeConfig.regionalScope?.trim();
    let aliasKey = scope ? `${baseAlias}-${scope.toLowerCase()}` : baseAlias;
    if (usedAliases.has(aliasKey)) {
      // Appended onto the scope-qualified alias (not restarted from baseAlias) so
      // region context isn't thrown away just because it also needed a further
      // disambiguator -- "geo-us-clienttwo", not "geo-clienttwo".
      const client = representativeConfig.clientName?.trim();
      aliasKey = client ? `${aliasKey}-${client.toLowerCase().replace(/\s+/g, '')}` : aliasKey;
    }
    usedAliases.add(aliasKey);
    usedAliasesByBase.set(baseAlias, usedAliases);
    return { aliasKey, channels, weeks };
  });
};

// One row per week for a channel's Excel export -- Date plus Spend/Impressions
// per platform, using the MMM's own (not BPM canonical) per-platform values, since
// the point is spot-checking what actually went into the model against the ad
// platform's own reporting. Callers pass already fencepost-filtered weeks.
export const buildChannelExportRows = (
  channel: ChannelConfig,
  weeks: WeekRow[]
): Record<string, string | number>[] => {
  const platformKeys = new Set<string>();
  weeks.forEach(week => {
    Object.keys(week.perChannel[channel.key]?.perPlatform ?? {}).forEach(key =>
      platformKeys.add(key)
    );
  });
  // Declared-taxonomy order first (matches the channel's own platform list), then
  // anything else seen in the data but not in the taxonomy, alphabetically --
  // shouldn't happen, but keeps every platform with real data visible rather than
  // silently dropping it.
  const taxonomyKeys = channel.platforms.map(p => p.key).filter(key => platformKeys.has(key));
  const extraKeys = [...platformKeys]
    .filter(key => !taxonomyKeys.includes(key))
    .sort((a, b) => a.localeCompare(b));
  const orderedKeys = [...taxonomyKeys, ...extraKeys];
  const platformLabel = (key: string): string =>
    channel.platforms.find(p => p.key === key)?.label ?? key;

  const sortedWeeks = [...weeks].sort((a, b) => a.weekIso.localeCompare(b.weekIso));

  if (orderedKeys.length === 0) {
    // No platform breakdown ever resolved for this channel -- fall back to the
    // channel-level total rather than exporting a Date-only sheet.
    return sortedWeeks.map(week => ({
      Date: week.weekIso,
      Spend: week.perChannel[channel.key]?.spend ?? 0,
      Impressions: week.perChannel[channel.key]?.impressions ?? 0
    }));
  }

  return sortedWeeks.map(week => {
    const perPlatform = week.perChannel[channel.key]?.perPlatform ?? {};
    const row: Record<string, string | number> = { Date: week.weekIso };
    orderedKeys.forEach(key => {
      const label = platformLabel(key);
      row[`${label} Spend`] = perPlatform[key]?.spend ?? 0;
      row[`${label} Impressions`] = perPlatform[key]?.impressions ?? 0;
    });
    return row;
  });
};

// A demand-group-split channel's key already ends with the exact suffix the backend's
// resolveChannelKey appends (e.g. "paidSearchDemandGeneration") -- strip it and use a
// short "Dg"/"Dc" suffix instead for the sheet name, matching the app's own "(DG)"/"(DC)"
// label convention, rather than the much longer raw key suffix (which the same Excel
// 31-char sheet-name limit would otherwise truncate into something unreadable, e.g.
// "dtc-comm-be_paidsearchdemandgen"). A channel that never splits (rawLabel === label)
// keeps its plain key unchanged.
const DEMAND_GROUP_KEY_SUFFIX: Record<'demandGeneration' | 'demandCapture', string> = {
  demandGeneration: 'DemandGeneration',
  demandCapture: 'DemandCapture'
};
const DEMAND_GROUP_SHEET_SUFFIX: Record<'demandGeneration' | 'demandCapture', string> = {
  demandGeneration: 'Dg',
  demandCapture: 'Dc'
};
export const channelSheetKey = (channel: ChannelConfig): string => {
  if (channel.label === channel.rawLabel) return channel.key;
  const keySuffix = DEMAND_GROUP_KEY_SUFFIX[channel.group];
  const base = channel.key.endsWith(keySuffix)
    ? channel.key.slice(0, -keySuffix.length)
    : channel.key;
  return `${base}${DEMAND_GROUP_SHEET_SUFFIX[channel.group]}`;
};

// One sheet per (fingerprint group x channel), named "<aliasKey>_<channelSheetKey>" --
// exportMultiSheetToExcel handles the Excel 31-char sheet-name limit/de-dup itself.
export const buildChannelExportSheets = (models: ChannelExportModel[]): Sheet[] =>
  groupModelsByChannelsFingerprint(models).flatMap(({ aliasKey, channels, weeks }) =>
    channels.map(channel => ({
      name: `${aliasKey}_${channelSheetKey(channel)}`,
      data: buildChannelExportRows(channel, weeks)
    }))
  );
